import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Globe, 
  Calendar, 
  MapPin, 
  User, 
  Hash, 
  FileText,
  Download,
  Copy,
  CheckCircle,
  ArrowLeft,
  Eye
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PublicNoticeDisclosure from '../components/PublicNoticeDisclosure';

const PublicNoticeViewPage = () => {
  const { id } = useParams();
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  // Fetch notice details
  useEffect(() => {
    const fetchNotice = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notices/public/${id}`);
        
        if (response.ok) {
          const data = await response.json();
          setNotice(data.notice);
        } else {
          setError('Notice not found or no longer available');
        }
      } catch (error) {
        console.error('Error fetching notice:', error);
        setError('Failed to load notice');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchNotice();
    }
  }, [id]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleDownloadPDF = async () => {
    if (!notice) return;

    try {
      // Call the backend PDF generation endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notices/public/${notice.id}/download-pdf`);
      
      if (response.ok) {
        const blob = await response.blob();
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        
        // Get filename from response headers or create default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'Public_Notice.pdf';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        } else {
          // Create filename from notice title
          const safeTitle = notice.title.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
          filename = `${safeTitle}_Public_Notice.pdf`;
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        
        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
      } else {
        console.error('Failed to download PDF:', response.status);
        alert('Failed to download PDF. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const getNoticeTypeIcon = (type) => {
    return <FileText className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading notice...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !notice) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">Notice Not Found</h3>
            <p className="text-gray-500 mb-4">
              {error || 'This notice may have been removed or the link is incorrect.'}
            </p>
            <Button onClick={() => window.history.back()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        {/* Public Notice Disclosure */}
        <PublicNoticeDisclosure variant="default" />
        
        {/* Back Navigation */}
        <div className="mb-6">
          <Button 
            variant="outline" 
            onClick={() => window.history.back()}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Public Notices</span>
          </Button>
        </div>

        {/* Main Notice Display */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader className="pb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                {getNoticeTypeIcon(notice.notice_type)}
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-2xl text-gray-900 mb-3">{notice.title}</CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="text-sm">
                      {notice.notice_type}
                    </Badge>
                    {notice.is_featured && (
                      <Badge className="bg-yellow-100 text-yellow-700 text-sm">
                        Featured
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Metadata */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <div>
                    <div className="font-medium text-gray-700">Published</div>
                    <div className="text-gray-600">{new Date(notice.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Eye className="w-4 h-4 text-gray-500" />
                  <div>
                    <div className="font-medium text-gray-700">Views</div>
                    <div className="text-gray-600">{notice.view_count}</div>
                  </div>
                </div>

                {notice.user_name && (
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <div>
                      <div className="font-medium text-gray-700">Published by</div>
                      <div className="text-gray-600">{notice.user_name}</div>
                    </div>
                  </div>
                )}

                {notice.location && (
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <div>
                      <div className="font-medium text-gray-700">Location</div>
                      <div className="text-gray-600">{notice.location}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Hash Proof */}
            {notice.hash_proof && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Hash className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-blue-800 mb-1">Sovereign Proof Hash</h3>
                    <p className="text-xs text-blue-700 mb-2">
                      This cryptographic hash provides immutable proof of publication timestamp and content integrity for legal verification.
                    </p>
                    <div className="bg-white border border-blue-200 rounded p-2 font-mono text-xs text-blue-900 break-all">
                      {notice.hash_proof}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Content */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Notice Content</h3>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                {notice.body_text ? (
                  <div className="prose max-w-none">
                    <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                      {notice.body_text}
                    </div>
                  </div>
                ) : notice.pdf_upload ? (
                  <div className="w-full">
                    <div className="mb-4 text-center">
                      <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <h4 className="text-lg font-semibold text-gray-900">PDF Document Content</h4>
                      <p className="text-sm text-gray-600">Full document content displayed below</p>
                    </div>
                    
                    {/* PDF Iframe Viewer */}
                    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm">
                      <iframe
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/notices/public/${notice.id}/pdf-content`}
                        className="w-full h-96 md:h-[600px]"
                        title="PDF Document Content"
                        frameBorder="0"
                        onError={(e) => {
                          console.error('PDF iframe error:', e);
                          // Fallback to download message if iframe fails
                          e.target.style.display = 'none';
                          const fallbackDiv = document.createElement('div');
                          fallbackDiv.className = 'text-center py-8';
                          fallbackDiv.innerHTML = `
                            <div class="text-gray-600">
                              <p>Unable to display PDF content inline.</p>
                              <p class="text-sm text-gray-500 mt-2">Please use the Download PDF button below to view the document.</p>
                            </div>
                          `;
                          e.target.parentNode.appendChild(fallbackDiv);
                        }}
                      />
                    </div>
                    
                    <div className="mt-3 text-center">
                      <p className="text-xs text-gray-500">
                        If the PDF doesn't display correctly, use the "Download PDF" button below to view it in your browser or PDF viewer.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No content available for this notice.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-500 flex items-center">
                <Globe className="w-4 h-4 mr-1" />
                Public Record #{notice.id.substring(0, 8)}
              </div>
              
              <div className="flex space-x-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleCopyLink}
                  className="flex items-center space-x-2"
                >
                  {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span>{copied ? 'Copied!' : 'Copy Link'}</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDownloadPDF}
                  className="flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Download PDF</span>
                </Button>
                
                <Button 
                  onClick={() => window.location.href = '/public-notices'}
                  className="flex items-center space-x-2"
                >
                  <Globe className="w-4 h-4" />
                  <span>View All Notices</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default PublicNoticeViewPage;